import React, { useState } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { Input } from '../../components/common/Input';
import { Modal } from '../../components/common/Modal';
import { useAgentSales } from '../../hooks/useAgent';
import { TicketReceipt } from '../../components/agent/TicketReceipt';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { History, Printer } from 'lucide-react';

export const AgentSales: React.FC = () => {
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewTickets, setViewTickets] = useState<any>(null);
  const { data, isLoading } = useAgentSales({ page, limit: 20, startDate: startDate || undefined, endDate: endDate || undefined });

  const sales = data?.data?.sales || [];
  const pagination = data?.data?.pagination;

  return (
    <AgentLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Sales</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">History of tickets you've sold</p>
      </div>

      <Card className="mb-6">
        <div className="flex items-end gap-4">
          <Input label="Start Date" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
          <Input label="End Date" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
          {(startDate || endDate) && <Button variant="outline" onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}>Reset</Button>}
        </div>
      </Card>

      {isLoading ? <Spinner size="lg" /> : sales.length === 0 ? (
        <Card><div className="text-center py-12"><History className="h-10 w-10 text-gray-200 mx-auto mb-2" /><p className="text-gray-400 dark:text-gray-500">No sales yet.</p></div></Card>
      ) : (
        <>
          <div className="space-y-3">
            {sales.map((sale: any) => (
              <Card key={sale.id}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{sale.raffle?.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{sale.customer?.firstName} {sale.customer?.lastName} · {sale.customer?.phone}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(sale.createdAt)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatCurrency(sale.totalAmount, sale.raffle?.currency || 'USD')}</p>
                    <p className="text-sm text-emerald-600">+{formatCurrency(sale.commissionAmount, sale.raffle?.currency || 'USD')} comm.</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{sale.quantity} ticket{sale.quantity > 1 ? 's' : ''}</p>
                  </div>
                </div>
                {sale.tickets && sale.tickets.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                    {sale.tickets.map((t: any) => (
                      <span key={t.id} className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg font-mono text-xs">
                        {t.ticketNumber} <Badge variant={t.status === 'WON' ? 'success' : t.status === 'LOST' ? 'danger' : 'default'}>{t.status}</Badge>
                      </span>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => setViewTickets(sale)}>
                      <Printer className="h-3.5 w-3.5 mr-1" />View & Print
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Previous</Button>
              <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
              <Button variant="outline" disabled={page === pagination.pages} onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Next</Button>
            </div>
          )}
        </>
      )}
      {/* Ticket Image Modal */}
      <Modal isOpen={!!viewTickets} onClose={() => setViewTickets(null)} title="Ticket Receipts">
        <div className="grid grid-cols-1 gap-4 max-h-[70vh] overflow-y-auto">
          {viewTickets?.tickets?.map((t: any) => (
            <TicketReceipt
              key={t.id}
              ticketNumber={t.ticketNumber}
              qrPayload={t.qrPayload}
              raffleName={viewTickets.raffle?.name || ''}
              customerName={`${viewTickets.customer?.firstName} ${viewTickets.customer?.lastName}`}
              price={parseFloat(viewTickets.ticketPrice).toFixed(2)}
              currency={viewTickets.raffle?.currency || 'USD'}
              purchaseDate={formatDate(viewTickets.createdAt)}
            />
          ))}
        </div>
      </Modal>
    </AgentLayout>
  );
};
